/**
 * dxfBridge.dynamic.aspect — Phase 5.1 design carrier.
 *
 * Introduces the `DynamicAspect<T>` interface + registry described in
 * `docs/architecture/phase-5.1-dxfbridge-dynamic-aspect-design.md`.
 *
 * PR 5.1.a registers the first aspect (grip) and is **purely additive**
 * — no existing dispatch path is rewritten yet. Subsequent PRs
 * (5.1.b … 5.1.e) register more aspects and, once the registry is
 * proven, the per-aspect branches in `dxfBridge.dynamic.parser.ts` and
 * `dxfBridge.dynamic.writer.ts` collapse into a single
 * `findAspect(canonicalType)?.parse(...) / .write(...)` call.
 *
 * The interface intentionally matches the **actual signatures** of the
 * existing parsers/writers (which differ from the original design draft
 * by returning a single `DxfDynamicBlockObject` rather than a flat
 * `DxfRecord[]` list — the original draft over-generalised).
 */

import type { DxfDynamicBlockObject } from "@dxf-ai-editor/dxf-io";

/**
 * A self-contained dynamic-block aspect. `T` is the per-aspect runtime
 * payload type (`DynamicGrip`, `ConstraintParameter`, …) — opaque to
 * the dispatcher. `ParseCtx` / `WriteCtx` are per-aspect side-channel
 * types — `void` for aspects that need no extra context (e.g. grips),
 * a typed object for aspects that bridge cross-references (e.g.
 * user parameters need a `Map<handle, AssocVariable>` to resolve the
 * associated variable; user-param writers need a handle minter +
 * block-record handle).
 */
export interface DynamicAspect<T, ParseCtx = void, WriteCtx = void> {
  /** Stable identifier — used in logs, telemetry, and registry indexing. */
  readonly id:
    | "grip"
    | "constraint"
    | "user-parameter"
    | "properties-table"
    | "assoc-dep";

  /** Canonicalised class names this aspect handles (ACDBBLOCK* form). */
  readonly classes: ReadonlySet<string>;

  /**
   * Predicate over the canonicalised class string. Default behaviour
   * (when not overridden) is `classes.has(canonical)`.
   */
  matches(canonicalType: string): boolean;

  /**
   * Decode a DXF object into the typed payload. Optional — write-only
   * aspects (e.g. `properties-table` which has no symmetric standalone
   * parser; parsing happens inline in tier-2) leave this undefined.
   * Callers must check the presence before invoking.
   *
   * The `canonicalType` argument is the already-canonicalised class
   * string so per-aspect implementations don't need to recompute it.
   * `ctx` is the per-aspect parse context (e.g. `Map<handle,
   * AssocVariable>` for user-parameter aspect). Aspects with
   * `ParseCtx = void` pass `undefined`.
   */
  parse?(obj: DxfDynamicBlockObject, canonicalType: string, ctx: ParseCtx): T;

  /**
   * Re-emit the typed payload as a `DxfDynamicBlockObject`. Returns
   * `undefined` for aspects whose emission is conditional on payload
   * shape (e.g. `properties-table` skips when both `columns` and
   * `rows` are empty). `ctx` is the per-aspect write context.
   * Aspects with `WriteCtx = void` pass `undefined`.
   */
  write(value: T, ctx: WriteCtx): DxfDynamicBlockObject | undefined;
}

/**
 * Default registry — populated by individual aspect modules calling
 * `registerAspect(...)` at module load. The dispatcher iterates the
 * registry in registration order; aspects must not overlap in class
 * names (asserted at registration).
 */
const REGISTRY: DynamicAspect<unknown, unknown, unknown>[] = [];

/**
 * Register a new aspect. Throws if any of the aspect's `classes` is
 * already claimed by a previously-registered aspect. Idempotent for
 * the same aspect instance (re-registration with the identical object
 * reference is a no-op — supports hot module reload in dev).
 */
export function registerAspect<T, P, W>(aspect: DynamicAspect<T, P, W>): void {
  if (REGISTRY.some((existing) => existing === aspect)) return;
  for (const existing of REGISTRY) {
    for (const cls of aspect.classes) {
      if (existing.classes.has(cls)) {
        throw new Error(
          `[DynamicAspect] duplicate class registration: "${cls}" claimed ` +
            `by both "${existing.id}" and "${aspect.id}"`,
        );
      }
    }
  }
  REGISTRY.push(aspect as DynamicAspect<unknown, unknown, unknown>);
}

/**
 * Find the aspect that recognises the given canonical class string.
 * Returns `undefined` when no aspect matches (caller is responsible
 * for falling back to legacy dispatch).
 */
export function findAspect(
  canonicalType: string,
): DynamicAspect<unknown, unknown, unknown> | undefined {
  return REGISTRY.find((a) => a.matches(canonicalType));
}

/**
 * Look up an aspect by its stable `id`. Used on the **write** path
 * where the caller has a typed payload (e.g. `DynamicGrip`) but no
 * canonical class string — and knows which aspect should write that
 * payload type.
 */
export function findAspectById(
  id: DynamicAspect<unknown, unknown, unknown>["id"],
): DynamicAspect<unknown, unknown, unknown> | undefined {
  return REGISTRY.find((a) => a.id === id);
}

/**
 * Read-only view of the registry — exposed for tests / telemetry /
 * doc generation. Callers must not mutate the array.
 */
export function listAspects(): readonly DynamicAspect<
  unknown,
  unknown,
  unknown
>[] {
  return REGISTRY;
}

/**
 * Test-only helper to reset the registry. Production code MUST NOT
 * call this — it exists so tests can isolate aspect registration order.
 */
export function _resetAspectRegistryForTests(): void {
  REGISTRY.length = 0;
}
